import { Header } from './Header';
import { MainLayout } from './MainLayout';
import { StatusBar } from './StatusBar';
import {
  DndContext,
  DragOverlay,
  DragEndEvent,
  DragStartEvent,
  closestCenter,
} from '@dnd-kit/core';
import { useState } from 'react';
import { toast } from 'sonner';
import type { StorageFile } from '@/data/adapters/types';

interface AppLayoutProps {
  onStartOnboarding?: () => void;
}

export function AppLayout({ onStartOnboarding }: AppLayoutProps) {
  const [draggedFile, setDraggedFile] = useState<StorageFile | null>(null);

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;

    // Check if it's a file being dragged
    if (active.data.current?.type === 'file') {
      setDraggedFile(active.data.current.file);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    setDraggedFile(null);

    // Handle file drop on editor
    if (active.data.current?.type === 'file' && over?.id === 'editor-drop-zone') {
      const file = active.data.current.file as StorageFile;

      // Create a reference to the file that can be inserted
      const referenceText = `@${file.type === 'phrase' ? 'phrases' : 'documents'}/${file.name}`;

      // Dispatch a custom event that the Monaco editor can listen to
      window.dispatchEvent(
        new CustomEvent('insert-file-reference', {
          detail: { referenceText, file },
        })
      );

      toast.success(`Inserted reference: ${referenceText}`);
    }
  };

  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div className="flex h-screen flex-col overflow-hidden bg-background">
        <Header />
        <div className="flex-1 overflow-hidden">
          <MainLayout onStartOnboarding={onStartOnboarding} />
        </div>
        <StatusBar />
      </div>

      <DragOverlay>
        {draggedFile ? (
          <div className="flex items-center gap-2 px-3 py-2 bg-primary text-primary-foreground rounded-md shadow-lg border">
            <span className="text-sm font-medium">
              {draggedFile.type === 'phrase' ? '📝' : '📄'} {draggedFile.name}
            </span>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
